/**
 * 이미지 처리 유틸리티 함수
 * FR-IMAGE-001, FR-IMAGE-002 구현을 위한 유틸리티
 */

/**
 * 이미지 URL을 로드하여 HTMLImageElement 반환
 * @param src 이미지 소스 URL 또는 DataURL
 * @returns Promise<HTMLImageElement>
 */
export function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('이미지를 로드할 수 없습니다.'));
    img.src = src;
  });
}

/**
 * File을 DataURL로 변환
 * @param file 이미지 파일
 * @returns Promise<string> DataURL
 */
export function fileToDataURL(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result;
      if (typeof result === 'string') {
        resolve(result);
      } else {
        reject(new Error('파일을 읽을 수 없습니다.'));
      }
    };
    reader.onerror = () => reject(new Error('파일을 읽을 수 없습니다.'));
    reader.readAsDataURL(file);
  });
}

/**
 * 이미지 파일 검증
 * @param file 검증할 파일
 * @returns 검증 결과
 */
export function validateImageFile(file: File): { valid: boolean; error?: string } {
  const validTypes = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];

  if (!validTypes.includes(file.type)) {
    return {
      valid: false,
      error: `지원하지 않는 형식입니다: ${file.type}. PNG, JPEG, GIF, WebP만 지원합니다.`,
    };
  }

  // 파일 크기 제한 (50MB)
  const maxSize = 50 * 1024 * 1024;
  if (file.size > maxSize) {
    return {
      valid: false,
      error: '파일 크기가 너무 큽니다. 50MB 이하의 파일만 지원합니다.',
    };
  }

  return { valid: true };
}

/**
 * 이미지의 상하 여백(흰색/투명)을 감지하여 잘라낸 이미지 반환
 * @param img 원본 이미지
 * @param threshold 여백 판정 임계값 (0-255, 기본값 250)
 * @returns 잘라낸 이미지의 DataURL, 원본 너비, 잘라낸 높이
 */
export function trimImageVertical(
  img: HTMLImageElement,
  threshold: number = 250
): { dataUrl: string; width: number; height: number } {
  const canvas = document.createElement('canvas');
  canvas.width = img.width;
  canvas.height = img.height;

  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas context를 생성할 수 없습니다.');

  // 이미지 그리기
  ctx.drawImage(img, 0, 0);

  // 픽셀 데이터 가져오기
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const data = imageData.data;
  const width = canvas.width;
  const height = canvas.height;

  // 행이 여백인지 확인하는 함수 (단일 색상으로 가득 찬 행도 여백으로 처리)
  const isRowEmpty = (y: number): boolean => {
    let firstR = -1, firstG = -1, firstB = -1;
    let isSingleColor = true;

    for (let x = 0; x < width; x++) {
      const idx = (y * width + x) * 4;
      const r = data[idx];
      const g = data[idx + 1];
      const b = data[idx + 2];
      const a = data[idx + 3];

      // 투명하면 무시
      if (a < 10) continue;

      // 첫 번째 불투명 픽셀의 색상 저장
      if (firstR === -1) {
        firstR = r;
        firstG = g;
        firstB = b;
        continue;
      }

      // 이전 픽셀과 색상이 다르면 단일 색상이 아님
      if (Math.abs(r - firstR) > 5 || Math.abs(g - firstG) > 5 || Math.abs(b - firstB) > 5) {
        isSingleColor = false;
        break;
      }
    }

    // 단일 색상이면 여백으로 처리 (흰색, 검정색, 또는 모든 단색)
    return isSingleColor;
  };

  // 위에서부터 첫 번째 콘텐츠 행 찾기
  let topY = 0;
  for (let y = 0; y < height; y++) {
    if (!isRowEmpty(y)) {
      topY = y;
      break;
    }
  }

  // 아래에서부터 첫 번째 콘텐츠 행 찾기
  let bottomY = height - 1;
  for (let y = height - 1; y >= topY; y--) {
    if (!isRowEmpty(y)) {
      bottomY = y;
      break;
    }
  }

  // 잘라낼 영역이 없으면 원본 반환
  if (topY === 0 && bottomY === height - 1) {
    return {
      dataUrl: canvas.toDataURL('image/png'),
      width: img.width,
      height: img.height,
    };
  }

  // 약간의 여유 공간 추가 (2px)
  topY = Math.max(0, topY - 2);
  bottomY = Math.min(height - 1, bottomY + 2);

  const newHeight = bottomY - topY + 1;

  // 새 캔버스에 잘라낸 영역만 그리기
  const trimmedCanvas = document.createElement('canvas');
  trimmedCanvas.width = width;
  trimmedCanvas.height = newHeight;

  const trimmedCtx = trimmedCanvas.getContext('2d');
  if (!trimmedCtx) throw new Error('Canvas context를 생성할 수 없습니다.');

  trimmedCtx.drawImage(
    canvas,
    0, topY, width, newHeight,  // source
    0, 0, width, newHeight      // destination
  );

  return {
    dataUrl: trimmedCanvas.toDataURL('image/png'),
    width: width,
    height: newHeight,
  };
}

/**
 * 여러 이미지를 동일 너비로 맞추어 세로로 연결
 * @param images 연결할 이미지 배열
 * @param targetWidth 목표 너비 (기본값: 가장 넓은 이미지 또는 최대 800px)
 * @param trimVertical 상하 여백 자르기 여부 (기본값: false)
 * @returns 연결된 이미지의 DataURL
 */
export function concatenateImages(
  images: HTMLImageElement[],
  targetWidth?: number,
  trimVertical: boolean = false
): string {
  if (images.length === 0) return '';

  // 목표 너비 결정
  const maxOriginalWidth = Math.max(...images.map(img => img.width));
  const width = targetWidth ?? Math.min(maxOriginalWidth, 800);

  // trimVertical 옵션이 켜져 있으면 각 이미지를 먼저 잘라냄
  interface ProcessedImage {
    width: number;
    height: number;
    draw: (ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number) => void;
  }

  const processedImages: ProcessedImage[] = images.map(img => {
    if (trimVertical) {
      const trimmed = trimImageVertical(img);
      // trimmed dataUrl을 그리기 위해 임시 캔버스 사용
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = trimmed.width;
      tempCanvas.height = trimmed.height;
      const tempCtx = tempCanvas.getContext('2d');
      if (tempCtx) {
        const tempImg = new Image();
        tempImg.src = trimmed.dataUrl;
        // 동기적으로 처리하기 위해 원본 이미지에서 직접 잘라냄
        const originalCanvas = document.createElement('canvas');
        originalCanvas.width = img.width;
        originalCanvas.height = img.height;
        const origCtx = originalCanvas.getContext('2d');
        if (origCtx) {
          origCtx.drawImage(img, 0, 0);
        }
        return {
          width: trimmed.width,
          height: trimmed.height,
          draw: (ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number) => {
            // trimmed 영역을 그리기
            const trimData = trimImageVertical(img);
            const trimCanvas = document.createElement('canvas');
            trimCanvas.width = trimData.width;
            trimCanvas.height = trimData.height;
            const trimCtx = trimCanvas.getContext('2d');
            if (trimCtx) {
              // 원본에서 trim된 영역 찾기
              const srcCanvas = document.createElement('canvas');
              srcCanvas.width = img.width;
              srcCanvas.height = img.height;
              const srcCtx = srcCanvas.getContext('2d');
              if (srcCtx) {
                srcCtx.drawImage(img, 0, 0);
                const imageData = srcCtx.getImageData(0, 0, img.width, img.height);
                const data = imageData.data;
                const threshold = 250;

                const isRowEmpty = (row: number): boolean => {
                  let firstR = -1, firstG = -1, firstB = -1;
                  let isSingleColor = true;

                  for (let col = 0; col < img.width; col++) {
                    const idx = (row * img.width + col) * 4;
                    const r = data[idx];
                    const g = data[idx + 1];
                    const b = data[idx + 2];
                    const a = data[idx + 3];

                    if (a < 10) continue;

                    if (firstR === -1) {
                      firstR = r;
                      firstG = g;
                      firstB = b;
                      continue;
                    }

                    if (Math.abs(r - firstR) > 5 || Math.abs(g - firstG) > 5 || Math.abs(b - firstB) > 5) {
                      isSingleColor = false;
                      break;
                    }
                  }

                  return isSingleColor;
                };

                let topY = 0;
                for (let row = 0; row < img.height; row++) {
                  if (!isRowEmpty(row)) {
                    topY = row;
                    break;
                  }
                }
                topY = Math.max(0, topY - 2);

                ctx.drawImage(srcCanvas, 0, topY, img.width, trimData.height, x, y, w, h);
              }
            }
          }
        };
      }
    }
    return {
      width: img.width,
      height: img.height,
      draw: (ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number) => {
        ctx.drawImage(img, x, y, w, h);
      }
    };
  });

  // 각 이미지의 조정된 높이 계산
  const scaledHeights = processedImages.map(pImg => {
    const scale = width / pImg.width;
    return pImg.height * scale;
  });

  // 전체 높이 계산
  const totalHeight = scaledHeights.reduce((sum, h) => sum + h, 0);

  // Canvas 생성
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = totalHeight;

  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas context를 생성할 수 없습니다.');

  // 배경을 흰색으로 채우기 (투명 배경 방지)
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, width, totalHeight);

  // 이미지 순서대로 그리기
  let currentY = 0;
  processedImages.forEach((pImg, index) => {
    const scaledHeight = scaledHeights[index];
    pImg.draw(ctx, 0, currentY, width, scaledHeight);
    currentY += scaledHeight;
  });

  return canvas.toDataURL('image/png');
}

/**
 * 이미지 배열을 비동기로 로드하여 연결
 * @param files 이미지 파일 배열
 * @param targetWidth 목표 너비
 * @param onProgress 진행 상황 콜백
 * @returns Promise<{ dataUrl: string; width: number; height: number }>
 */
export async function loadAndConcatenateImages(
  files: File[],
  targetWidth?: number,
  onProgress?: (current: number, total: number) => void
): Promise<{ dataUrl: string; width: number; height: number }> {
  if (files.length === 0) {
    throw new Error('이미지 파일이 없습니다.');
  }

  const images: HTMLImageElement[] = [];

  for (let i = 0; i < files.length; i++) {
    const file = files[i];

    // 파일 검증
    const validation = validateImageFile(file);
    if (!validation.valid) {
      throw new Error(validation.error);
    }

    // DataURL로 변환 후 이미지 로드
    const dataUrl = await fileToDataURL(file);
    const img = await loadImage(dataUrl);
    images.push(img);

    // 진행 상황 콜백
    if (onProgress) {
      onProgress(i + 1, files.length);
    }
  }

  // 이미지 연결
  const resultDataUrl = concatenateImages(images, targetWidth);

  // 결과 이미지 크기 계산
  const maxOriginalWidth = Math.max(...images.map(img => img.width));
  const width = targetWidth ?? Math.min(maxOriginalWidth, 800);
  const scaledHeights = images.map(img => {
    const scale = width / img.width;
    return img.height * scale;
  });
  const height = scaledHeights.reduce((sum, h) => sum + h, 0);

  return { dataUrl: resultDataUrl, width, height };
}
